/**
 * Configurações do webchat, em tela cheia com navegação lateral.
 *
 * As cinco seções daqui viviam como abas de uma coluna de 384px, misturadas com o formulário do
 * bloco selecionado e com os números. Três consequências, todas visíveis:
 *
 *  - **os rótulos foram encurtados para caber** — "Aparência" virou "Estilo", "Instalação" virou
 *    "Instalar" — e ainda assim as abas quebravam em duas linhas;
 *  - campos que pedem lado a lado (as três cores, as duas distâncias) ficavam empilhados ou
 *    espremidos em três colunas de 100px;
 *  - o aviso de segurança dos domínios, que é o texto mais importante do painel, era lido numa
 *    coluna de 40 caracteres.
 *
 * Vertical à esquerda, cada seção tem a largura de que precisa e o nome de volta ao que era.
 *
 * A seção ativa está **na URL** (`/w/:id/config/dominios`). É o que permite mandar o link direto
 * para alguém, e o que faz o botão Voltar do navegador funcionar entre seções em vez de sair da
 * tela inteira.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { AppearanceSection } from '../components/settings/AppearanceSection.js';
import { DomainsSection } from '../components/settings/DomainsSection.js';
import { InstallSection } from '../components/settings/InstallSection.js';
import { LimitSection } from '../components/settings/LimitSection.js';
import { SecretsSection } from '../components/settings/SecretsSection.js';
import {
  IconeDominios,
  IconeEstilo,
  IconeInstalar,
  IconeLimite,
  IconeSegredos,
} from '../components/icons.js';
import { SideNav } from '../components/ui.js';
import { apiBase, widgetSrc } from '../lib/env.js';

const SECOES = [
  { id: 'aparencia', label: 'Aparência', icon: IconeEstilo },
  { id: 'dominios', label: 'Domínios', icon: IconeDominios },
  { id: 'segredos', label: 'Segredos', icon: IconeSegredos },
  { id: 'limite', label: 'Limite de conversas', icon: IconeLimite },
  { id: 'instalacao', label: 'Instalação', icon: IconeInstalar },
] as const;

type Secao = (typeof SECOES)[number]['id'];

const ehSecao = (valor: string | undefined): valor is Secao =>
  SECOES.some((secao) => secao.id === valor);

export function SettingsPage() {
  const { id, section } = useParams<{ id: string; section?: string }>();
  const navigate = useNavigate();

  // `/config` sem sufixo cai na primeira seção, que é a mais visitada.
  const ativa: Secao = ehSecao(section) ? section : 'aparencia';

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-8 lg:flex-row">
        {/*
          `lg:sticky` para a navegação acompanhar a rolagem: a seção de aparência é longa, e sem isso
          trocar de seção exigia rolar de volta ao topo.
        */}
        <aside className="w-full shrink-0 lg:sticky lg:top-8 lg:w-56 lg:self-start">
          <SideNav
            label="Seções das configurações"
            items={SECOES.map((secao) => ({ id: secao.id, label: secao.label, icon: secao.icon }))}
            active={ativa}
            onChange={(proxima) => navigate(`/w/${id}/config/${proxima}`)}
          />
        </aside>

        {/*
          `max-w-3xl` no conteúdo: o painel roda em monitor largo, e um campo de texto de 1200px é
          desconfortável de ler e de mirar. 768px mantém a linha entre 60 e 75 caracteres, que é a
          faixa em que a leitura é mais rápida.
        */}
        <main className="min-w-0 flex-1 lg:max-w-3xl">
          {ativa === 'aparencia' ? <AppearanceSection /> : null}
          {ativa === 'dominios' ? <DomainsSection /> : null}
          {ativa === 'segredos' ? <SecretsSection /> : null}
          {ativa === 'limite' ? <LimitSection /> : null}
          {ativa === 'instalacao' ? <InstallSection apiBase={apiBase} widgetSrc={widgetSrc} /> : null}
        </main>
      </div>
    </div>
  );
}
